// app/javascript/controllers/aircraft_selection_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["list", "hiddenField", "count", "emptyState"]
    static values = {
        maxAircraft: { type: Number, default: 50 }
    }

    connect() {
        this.selectedAircraft = []
        this.updateDisplay()
    }

    // Called when user selects an aircraft from autocomplete
    addAircraft(event) {
        const aircraft = event.detail || event.currentTarget.dataset

        // Check if already selected
        if (this.selectedAircraft.some(a => a.nNumber === aircraft.nNumber)) {
            this.showMessage(`${"N" + aircraft.nNumber} is already selected`, "warning")
            return
        }

        // Check max limit (BR-001)
        if (this.selectedAircraft.length >= this.maxAircraftValue) {
            this.showMessage(`Maximum ${this.maxAircraftValue} aircraft allowed`, "error")
            return
        }

        // Add to selection
        this.selectedAircraft.push({
            nNumber: aircraft.nNumber,
            makeModel: aircraft.makeModel || "Unknown",
            year: aircraft.year || ""
        })

        this.updateDisplay()
        this.announceToScreenReader(`Added ${"N" + aircraft.nNumber}`)
    }

    // Remove aircraft from selection
    removeAircraft(event) {
        const nNumber = event.currentTarget.dataset.nNumber

        this.selectedAircraft = this.selectedAircraft.filter(
            aircraft => aircraft.nNumber !== nNumber
        )

        this.updateDisplay()
        this.announceToScreenReader(`Removed ${"N" + nNumber}`)
    }

    // Update the UI and hidden field
    updateDisplay() {
        this.updateList()
        this.updateHiddenField()
        this.updateCount()
        this.toggleEmptyState()
    }

    updateList() {
        if (!this.hasListTarget) return

        this.listTarget.innerHTML = this.selectedAircraft
            .map(aircraft => this.aircraftItemHTML(aircraft))
            .join("")
    }

    aircraftItemHTML(aircraft) {
        const yearDisplay = aircraft.year ? `(${aircraft.year})` : ""

        return `
      <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <span class="font-semibold text-gray-900">${"N" + this.escapeHtml(aircraft.nNumber)}</span>
            <span class="text-sm text-gray-600">${this.escapeHtml(aircraft.makeModel)}</span>
            ${yearDisplay ? `<span class="text-sm text-gray-500">${yearDisplay}</span>` : ''}
          </div>
        </div>
        <button
          type="button"
          data-action="click->aircraft-selection#removeAircraft"
          data-n-number="${this.escapeHtml(aircraft.nNumber)}"
          class="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
          aria-label="Remove ${this.escapeHtml(aircraft.nNumber)}"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `
    }

    updateHiddenField() {
        if (!this.hasHiddenFieldTarget) return

        // Update hidden field with array of N-numbers
        const nNumbers = this.selectedAircraft.map(a => a.nNumber)
        this.hiddenFieldTarget.value = JSON.stringify(nNumbers)
    }

    updateCount() {
        if (!this.hasCountTarget) return

        const count = this.selectedAircraft.length
        const plural = count === 1 ? "aircraft" : "aircraft"
        const maxDisplay = ` (max ${this.maxAircraftValue})`

        this.countTarget.textContent = count > 0
            ? `${count} ${plural} selected${maxDisplay}`
            : `No aircraft selected (optional)${maxDisplay}`

        // Update styling based on count
        if (count >= this.maxAircraftValue) {
            this.countTarget.classList.add("text-orange-600", "font-medium")
        } else {
            this.countTarget.classList.remove("text-orange-600", "font-medium")
        }
    }

    toggleEmptyState() {
        if (!this.hasEmptyStateTarget) return

        if (this.selectedAircraft.length === 0) {
            this.emptyStateTarget.classList.remove("hidden")
            this.listTarget.classList.add("hidden")
        } else {
            this.emptyStateTarget.classList.add("hidden")
            this.listTarget.classList.remove("hidden")
        }
    }

    // Utility methods
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    showMessage(message, type = "info") {
        // You can implement this based on your notification system
        console.log(`[${type}] ${message}`)
        // Example: dispatch event for toast notification
        this.dispatch("message", { detail: { message, type } })
    }

    announceToScreenReader(message) {
        // Create temporary element for screen reader announcement
        const announcement = document.createElement('div')
        announcement.setAttribute('role', 'status')
        announcement.setAttribute('aria-live', 'polite')
        announcement.className = 'sr-only'
        announcement.textContent = message
        document.body.appendChild(announcement)
        setTimeout(() => announcement.remove(), 1000)
    }
}